import { useEffect, useState, useRef } from "react";
import { Eye, Lightbulb } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { loadingMessages } from "@/data/cosmoQuestions";
import { useGetScorecard } from "@/resources/scorecard";
import type { SPIResult } from "@/types/scorecard";

interface SPILoadingStateProps {
  scorecardId: number;
  onComplete: (result: SPIResult) => void;
  onError: () => void;
  initialElapsed?: number;
}

export function SPILoadingState({ scorecardId, onComplete, onError, initialElapsed }: SPILoadingStateProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [startTime] = useState(() => Date.now() - (initialElapsed ?? 0));
  const [displayProgress, setDisplayProgress] = useState(0);
  const [activeStep, setActiveStep] = useState<string | null>(null);
  const [isPulsing, setIsPulsing] = useState(false);
  const completingRef = useRef(false);

  const { data, error } = useGetScorecard(scorecardId);

  useEffect(() => {
    const msgInterval = setInterval(() => {
      setMessageIndex((prev) => (prev < loadingMessages.length - 1 ? prev + 1 : prev));
    }, 1500);
    return () => clearInterval(msgInterval);
  }, []);

  useEffect(() => {
    const frameInterval = setInterval(() => {
      if (completingRef.current) return;
      const elapsed = Date.now() - startTime;
      const raw = 1 - Math.exp(-elapsed / 30000);
      setDisplayProgress(Math.min(raw * 100, 95));
    }, 200);
    return () => clearInterval(frameInterval);
  }, [startTime]);

  useEffect(() => {
    if (!data) return;

    if (data.status === "completed") {
      completingRef.current = true;
      setDisplayProgress(100);
      const result = data as SPIResult & typeof data;
      const timer = setTimeout(() => {
        onComplete(result);
      }, 400);
      return () => clearTimeout(timer);
    } else if (data.status === "failed") {
      onError();
    } else if (data.status === "processing") {
      const step = (data as Record<string, unknown>).step;
      if (typeof step === "string" && step) {
        setActiveStep(step);
        setIsPulsing(true);
        const pulseTimer = setTimeout(() => {
          setIsPulsing(false);
        }, 1000);
        return () => clearTimeout(pulseTimer);
      }
    }
  }, [data, onComplete, onError]);

  useEffect(() => {
    if (!error) return;
    onError();
  }, [error, onError]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-sm p-8 text-center">
        <div className={`mx-auto mb-4 h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center ${isPulsing ? "animate-pulse" : ""}`}>
          <Eye className="h-7 w-7 text-primary" />
        </div>

        <h2 className="text-lg font-semibold text-foreground mb-4">
          Analyzing Product
        </h2>

        <Progress value={displayProgress} className="h-2 mb-2" />
        <p className="text-sm text-muted-foreground mb-4">
          {Math.round(displayProgress)}%
        </p>

        <div className="bg-muted/50 rounded-lg p-3 flex items-start gap-2 text-left">
          <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <p className="text-sm text-muted-foreground leading-relaxed">
            {activeStep !== null ? activeStep : loadingMessages[messageIndex]}
          </p>
        </div>
      </div>
    </div>
  );
}
